import { MAX_COUNTERPARTY_REFERENCES } from "@common/schemas/counterpartyReference.schema";
import { MAX_INVOICE_REFERENCES } from "@common/schemas/invoiceReference.schema";
import { MAX_TRANSACTION_REFERENCES } from "@common/schemas/transactionReference.schema";
import { buildCounterpartyReferences } from "./buildCounterpartyReferences";
import {
    AskAboutInvoice,
    buildInvoiceReferences,
} from "./buildInvoiceReferences";
import { buildTransactionReferences } from "./buildTransactionReferences";
import { useCounterpartyRefContextMaybe } from "./CounterpartyRefContext";
import { useInvoiceRefContextMaybe } from "./InvoiceRefContext";
import { useTransactionRefContextMaybe } from "./TransactionRefContext";
import { useAddToChat } from "./useAddToChat";

type ChatTransaction = Parameters<
    typeof buildTransactionReferences
>[0]["transactions"][number];
type ChatCounterparty = Parameters<
    typeof buildCounterpartyReferences
>[0]["counterparties"][number];

const buildTransactionRefs = ({
    taskId,
    items,
}: {
    taskId: string;
    items: ChatTransaction[];
}) => buildTransactionReferences({ taskId, transactions: items });

const buildInvoiceRefs = ({
    taskId,
    items,
}: {
    taskId: string;
    items: AskAboutInvoice[];
}) => buildInvoiceReferences({ taskId, invoices: items });

const buildCounterpartyRefs = ({
    taskId,
    items,
}: {
    taskId: string;
    items: ChatCounterparty[];
}) => buildCounterpartyReferences({ taskId, counterparties: items });

/** Adds transactions to the open task's chat; shared by the button and row drag. */
export function useAddTransactionsToChat() {
    const { canAddToChat, addToChat } = useAddToChat({
        draftRefsState: useTransactionRefContextMaybe(),
        maxRefs: MAX_TRANSACTION_REFERENCES,
        noun: "transaction",
        buildRefs: buildTransactionRefs,
    });
    return { canAddToChat, addTransactionsToChat: addToChat };
}

/** Adds invoices to the open task's chat; shared by the button and row drag. */
export function useAddInvoicesToChat() {
    const { canAddToChat, addToChat } = useAddToChat({
        draftRefsState: useInvoiceRefContextMaybe(),
        maxRefs: MAX_INVOICE_REFERENCES,
        noun: "invoice",
        buildRefs: buildInvoiceRefs,
    });
    return { canAddToChat, addInvoicesToChat: addToChat };
}

/** Adds counterparties to the open task's chat; shared by the button and row drag. */
export function useAddCounterpartiesToChat() {
    const { canAddToChat, addToChat } = useAddToChat({
        draftRefsState: useCounterpartyRefContextMaybe(),
        maxRefs: MAX_COUNTERPARTY_REFERENCES,
        noun: "counterparty",
        buildRefs: buildCounterpartyRefs,
    });
    return { canAddToChat, addCounterpartiesToChat: addToChat };
}
